import React, { CSSProperties, FC } from 'react';
import styled from '@emotion/styled';

interface OuterProps {
    checked?: boolean;
    disabled?: boolean;
    color?: string;
}

const Outer = styled.button<OuterProps>`
    position: relative;
    display: inline-block;
    box-sizing: border-box;
    width: 44px;
    height: 22px;
    line-height: 20px;
    padding: 0;
    vertical-align: middle;
    border-radius: 20px 20px;
    border: 1px solid ${({ checked, disabled, color }) => (checked && !disabled ? color : '#ccc')};
    background-color: ${({ checked, disabled, color }) => (checked && !disabled ? color : '#ccc')};
    cursor: ${({ disabled }) => (disabled ? 'no-drop' : 'pointer')};
    transition: all 0.3s cubic-bezier(0.35, 0, 0.25, 1);

    &:after {
        position: absolute;
        width: 18px;
        height: 18px;
        left: ${({ checked }) => (checked ? '22px' : '2px')};
        top: 1px;
        border-radius: 50% 50%;
        background-color: ${({ disabled }) => (disabled ? '#9e9e9e' : '#fff')};
        content: ' ';
        cursor: ${({ disabled }) => (disabled ? 'no-drop' : 'pointer')};
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.26);
        transform: scale(1);
        transition: left 0.3s cubic-bezier(0.35, 0, 0.25, 1);
        animation-timing-function: cubic-bezier(0.35, 0, 0.25, 1);
        animation-duration: 0.3s;
        animation-name: ${({ disabled }) => (disabled ? 'none' : 'rcSwitchOff')};
    }

    &:hover:after {
        transform: ${({ disabled }) => (disabled ? 'scale(1)' : 'scale(1.1)')};
        animation-name: ${({ disabled }) => (disabled ? 'none' : 'rcSwitchOn')};
    }

    &:focus {
        box-shadow: 0 0 0 2px tint(#2db7f5, 80%);
        outline: none;
    }

    @keyframes rcSwitchOn {
        0% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.25);
        }
        100% {
            transform: scale(1.1);
        }
    }

    @keyframes rcSwitchOff {
        0% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
        }
    }
`;

const Inner = styled.span<{ checked?: boolean }>`
    color: #fff;
    font-size: 12px;
    position: absolute;
    left: ${({ checked }) => (checked ? '6px' : '24px')};
    top: 0;
`;

interface Props {
    disabled?: boolean;
    checked?: boolean;
    onChange: (checked: boolean) => void;
    style?: CSSProperties;
    color?: string;
    testId?: string;
}

const Switch: FC<Props> = ({ disabled, checked, onChange, style, color = '#87d068', testId }) => {
    return (
        <Outer
            data-testid={testId}
            type="button"
            role="switch"
            checked={checked}
            disabled={disabled}
            onClick={() => onChange(!checked)}
            color={color}
            style={style}>
            <Inner checked={checked} />
        </Outer>
    );
};

export default Switch;
